import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtUSD, fmtDateTime, inquiryStatusClass } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowUpRight, LogIn } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import MonthlyPerformanceReport from "@/components/MonthlyPerformanceReport";

const TabBar = ({ tabs, active, onChange }) => (
  <div className="flex gap-8 border-b border-border" data-testid="dashboard-tabs">
    {tabs.map((t) => (
      <button
        key={t.value}
        className="side-tab"
        data-state={active === t.value ? "active" : "inactive"}
        onClick={() => onChange(t.value)}
        data-testid={`tab-${t.value}`}
      >
        <span>{t.label}</span>
      </button>
    ))}
  </div>
);

const KPI = ({ label, value, sub, testId }) => (
  <div className="card-sharp p-6" data-testid={testId}>
    <div className="label-mono">{label}</div>
    <div className="font-display text-3xl mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

// Stable recharts style refs — avoid the inline-object rerender penalty flagged by code review.
const LINE_DOT = { r: 4, fill: "#0047FF" };
const BAR_MARGIN = { left: 8 };

const bookingStatusKlass = (s) => {
  if (s === "paid") return "bg-[#ac62ea]/10 text-[#ac62ea] border-[#ac62ea]/30";
  if (s === "free") return "bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/30";
  return "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/30";
};
const bookingStatusLabel = (s) => (s === "awaiting_payment" ? "awaiting payment" : s);

export default function ClientDashboard() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("side-a");
  const [metrics, setMetrics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [artifacts, setArtifacts] = useState([]);

  // Admins land here only via direct nav / bookmark. Forward them to /admin so
  // the role mismatch (Revenue Performance Studio dashboard vs Admin console)
  // never confuses them.
  useEffect(() => {
    if (user?.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/dashboard/client-metrics").then((r) => setMetrics(r.data)),
      api.get("/bookings/me").then((r) => setBookings(r.data)),
      api.get("/inquiries/me").then((r) => setInquiries(r.data)),
      api.get("/dashboard/artifacts").then((r) => setArtifacts(r.data)),
    ]).catch(() => {});
  }, [user]);

  const tabs = useMemo(
    () => [
      { value: "side-a", label: "Revenue" },
      { value: "side-b", label: "Engagements" },
      { value: "artifacts", label: `Artifacts${artifacts.length ? ` · ${artifacts.length}` : ""}` },
    ],
    [artifacts.length],
  );

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  // Centralize the sign-in UX on /login so both Google and Magic Link pathways
  // are always available, regardless of which protected page the visitor lands on.
  if (!user) {
    return <Navigate to="/login?return_to=%2Fdashboard" replace data-testid="dashboard-login-redirect" />;
  }

  const payQuote = async (quote_id) => {
    setPayingId(quote_id);
    try {
      const res = await api.post(`/quotes/${quote_id}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch {
      setPayingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="client-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">Revenue Performance Studio</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-[-0.035em] font-semibold">Hello, {user.name.split(" ")[0]}.</h1>
        </div>
        <Button onClick={() => navigate("/services")} variant="outline" className="rounded-sm" data-testid="book-new-session">
          Book new session <ArrowUpRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="mt-10">
        <TabBar active={tab} onChange={setTab} tabs={tabs} />

        {tab === "side-a" && metrics && (
          <div className="mt-8 space-y-6" data-testid="side-a-content">
            <MonthlyPerformanceReport metrics={metrics} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Invested in growth" value={fmtUSD(metrics.total_invested)} sub={`${metrics.engagements_count} engagements`} testId="kpi-invested" />
              <KPI label="Consulting cost" value={fmtUSD(metrics.total_cost)} sub="Studio time + tooling" testId="kpi-cost" />
              <KPI label="Net margin uplift" value={fmtUSD(metrics.margin)} sub={`${metrics.margin_pct}% of invest`} testId="kpi-margin" />
              <KPI label="Avg value / engagement" value={fmtUSD(metrics.value_per_engagement)} testId="kpi-avg" />
            </div>

            <div className="grid lg:grid-cols-5 gap-4">
              <div className="card-sharp p-6 lg:col-span-3" data-testid="chart-monthly">
                <div className="flex justify-between items-end">
                  <div>
                    <div className="label-mono">Monthly investment</div>
                    <div className="font-display text-xl mt-1">Revenue program spend</div>
                  </div>
                </div>
                <div className="mt-6 h-64">
                  {metrics.monthly.length > 0 ? (
                    <ResponsiveContainer>
                      <LineChart data={metrics.monthly}>
                        <CartesianGrid stroke="#eee" vertical={false} />
                        <XAxis dataKey="month" stroke="#888" fontSize={12} />
                        <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                        <Tooltip formatter={(v) => fmtUSD(v)} />
                        <Line type="monotone" dataKey="value" stroke="#0047FF" strokeWidth={2.5} dot={LINE_DOT} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No paid engagements yet.</div>
                  )}
                </div>
              </div>
              <div className="card-sharp p-6 lg:col-span-2" data-testid="chart-by-service">
                <div className="label-mono">Spend by engagement</div>
                <div className="font-display text-xl mt-1">Service mix</div>
                <div className="mt-6 h-64">
                  {metrics.by_service.length > 0 ? (
                    <ResponsiveContainer>
                      <BarChart data={metrics.by_service} layout="vertical" margin={BAR_MARGIN}>
                        <CartesianGrid stroke="#eee" horizontal={false} />
                        <XAxis type="number" stroke="#888" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
                        <YAxis type="category" dataKey="name" stroke="#0A0A0A" fontSize={11} width={120} />
                        <Tooltip formatter={(v) => fmtUSD(v)} />
                        <Bar dataKey="value" fill="#0A0A0A" radius={0} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No data yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "side-b" && (
          <div className="mt-8 space-y-10" data-testid="side-b-content">
            <div>
              <div className="label-mono">Bookings</div>
              <h2 className="font-display text-2xl mt-1">Your sessions</h2>
              <div className="mt-4 card-sharp">
                {bookings.length === 0 ? (
                  <div className="p-8 text-sm text-muted-foreground">No bookings yet. <button onClick={() => navigate("/services")} className="text-[#0047FF] hover:underline">Browse services →</button></div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left border-b border-border">
                      <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="p-4">Service</th>
                        <th className="p-4">Submitted</th>
                        <th className="p-4">Amount</th>
                        <th className="p-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr key={b.id} className="border-b border-border last:border-0" data-testid={`booking-row-${b.id}`}>
                          <td className="p-4 font-medium">{b.service_title}</td>
                          <td className="p-4">{fmtDateTime(b.created_at)}</td>
                          <td className="p-4">{b.amount > 0 ? fmtUSD(b.amount) : <span className="text-muted-foreground">—</span>}</td>
                          <td className="p-4">
                            <span className={`px-2 py-0.5 text-xs rounded-sm border ${bookingStatusKlass(b.payment_status)}`}>{bookingStatusLabel(b.payment_status)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div>
              <div className="label-mono">Custom Projects</div>
              <h2 className="font-display text-2xl mt-1">Your inquiries &amp; quotes</h2>
              <div className="mt-4 space-y-3">
                {inquiries.length === 0 && (
                  <div className="card-sharp p-8 text-sm text-muted-foreground">No inquiries yet. <button onClick={() => navigate("/inquiry")} className="text-[#0047FF] hover:underline">Send a brief →</button></div>
                )}
                {inquiries.map((i) => (
                  <div key={i.id} className="card-sharp p-6" data-testid={`inquiry-row-${i.id}`}>
                    <div className="flex justify-between items-start gap-4 flex-wrap">
                      <div>
                        <div className="label-mono">{i.business_name}</div>
                        <div className="font-display text-xl mt-1">{i.goals.slice(0, 70)}{i.goals.length > 70 ? "…" : ""}</div>
                        <div className="text-xs text-muted-foreground mt-1">Budget {i.budget} · Timeline {i.timeline}</div>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-sm border ${inquiryStatusClass(i.status)}`}>{i.status}</span>
                    </div>
                    {i.quote && (
                      <div className="mt-5 pt-5 border-t border-border flex flex-wrap gap-4 items-end justify-between">
                        <div>
                          <div className="label-mono">Quote</div>
                          <div className="font-display text-lg mt-1">{i.quote.title} — {fmtUSD(i.quote.amount)}</div>
                          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{i.quote.scope}</p>
                        </div>
                        {i.quote.status !== "paid" && (
                          <Button onClick={() => payQuote(i.quote.id)} disabled={payingId === i.quote.id} className="rounded-sm bg-[#0047FF] hover:bg-[#6a36b3]" data-testid={`pay-quote-${i.quote.id}`}>
                            {payingId === i.quote.id ? "Redirecting…" : "Pay invoice"}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "artifacts" && (
          <div className="mt-8 space-y-6" data-testid="artifacts-content">
            {artifacts.length === 0 ? (
              <div className="card-sharp p-8 text-sm text-muted-foreground text-center" data-testid="artifacts-empty">
                No artifacts shared with you yet. As soon as your engagement lead uploads a deliverable, you&apos;ll see it here.
              </div>
            ) : (
              Object.entries(
                artifacts.reduce((acc, a) => {
                  const key = `${a.ref_type}:${a.ref_id}`;
                  if (!acc[key]) acc[key] = { title: a.ref_title, type: a.ref_type, items: [] };
                  acc[key].items.push(a);
                  return acc;
                }, {})
              ).map(([key, group]) => (
                <div key={key} className="card-sharp p-6" data-testid={`artifact-group-${key}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <div>
                      <div className="label-mono">{group.type === "booking" ? "Booking" : "Project"}</div>
                      <div className="font-display text-xl mt-1">{group.title}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{group.items.length} {group.items.length === 1 ? "item" : "items"}</div>
                  </div>
                  <ul className="mt-5 space-y-3">
                    {group.items.map((a) => (
                      <li key={a.id} className="flex items-start justify-between gap-4 p-3 rounded-sm border border-border" data-testid={`client-artifact-${a.id}`}>
                        <div className="min-w-0 flex-1">
                          <a href={a.url} target="_blank" rel="noreferrer" className="font-medium text-[#0047FF] hover:underline break-all">{a.title}</a>
                          {a.note && <div className="text-sm text-muted-foreground mt-1">{a.note}</div>}
                          <div className="text-xs text-muted-foreground mt-1">Shared {new Date(a.uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                        </div>
                        <Button asChild size="sm" variant="outline" className="rounded-sm shrink-0">
                          <a href={a.url} target="_blank" rel="noreferrer" data-testid={`open-artifact-${a.id}`}>Open <ArrowUpRight className="ml-1 h-3 w-3" /></a>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
